import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { getDocs, getFirestore, query, where } from "firebase/firestore";
import { useCallback, useMemo, useState } from "react";
import { queryKeys } from "../../../queries/queryKeys";
import { formatDate, formatearNumeros } from "../../../utils/formatters";
import { calcularGastosDelDia, obtenerGastosDelDiaDeRuta } from "../../../utils/rutaGastos";
import {
  calcularMetricasMovimientosDelDia,
  calcularSaldoActualRuta,
  crearMovimientosRutaCollectionRef,
  type MovimientoRutaCompatible,
  obtenerTotalesRuta,
} from "../../../utils/rutaMovimientos";
import type {
  AnyObject,
  ClienteEstadistica,
  EstadisticasMetricas,
  EstadisticasModalProps,
  UseEstadisticasModalReturn,
} from "./types";

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const esMismoDia = (fechaA: Date, fechaB: Date): boolean =>
  format(fechaA, "yyyy-MM-dd") === format(fechaB, "yyyy-MM-dd");

const normalizarFechaPago = (fecha: any): Date | null => {
  if (fecha instanceof Date) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    return fecha.toDate();
  }

  const fechaConvertida = new Date(fecha);

  return Number.isNaN(fechaConvertida.getTime()) ? null : fechaConvertida;
};

const calcularAbonoDelDiaCliente = (cliente: ClienteEstadistica, fechaReferencia: Date): number => {
  const historialPagos: AnyObject[] = Array.isArray(cliente.historialPagos)
    ? cliente.historialPagos
    : [];

  return historialPagos.reduce((total, pago) => {
    const fechaPago = normalizarFechaPago(pago?.fecha);

    if (!fechaPago || !esMismoDia(fechaPago, fechaReferencia)) {
      return total;
    }

    return total + normalizarNumero(pago?.abono);
  }, 0);
};

const calcularMetricasClientes = (
  clientes: ClienteEstadistica[] | null,
  fechaHoy: string,
  fechaReferencia: Date,
): Pick<EstadisticasMetricas, "prestamoDelDia" | "totalAbonos" | "totalClientes"> => {
  if (!clientes?.length) {
    return {
      prestamoDelDia: 0,
      totalAbonos: 0,
      totalClientes: 0,
    };
  }

  return clientes.reduce<
    Pick<EstadisticasMetricas, "prestamoDelDia" | "totalAbonos" | "totalClientes">
  >(
    (metricas, cliente) => {
      metricas.totalAbonos += calcularAbonoDelDiaCliente(cliente, fechaReferencia);

      if (cliente.fechaActual === fechaHoy) {
        metricas.prestamoDelDia += normalizarNumero(cliente.valorPrestamo);
      }

      return metricas;
    },
    {
      prestamoDelDia: 0,
      totalAbonos: 0,
      totalClientes: clientes.length,
    },
  );
};

const calcularMetricasEstadisticas = (
  selectedRuta: EstadisticasModalProps["selectedRuta"],
  clientes: EstadisticasModalProps["clientes"],
  gastosHoy: number,
  metricasMovimientosDelDia: Pick<EstadisticasMetricas, "prestamoDelDia" | "totalAbonos">,
  fechaHoyIso: string,
  fechaActual: Date,
): EstadisticasMetricas => {
  const saldoMasNuevo = normalizarNumero(selectedRuta?.historialSaldos);
  const saldoActual = calcularSaldoActualRuta(selectedRuta);
  const { totalIngresos, totalSalidas } = obtenerTotalesRuta(selectedRuta);
  const { prestamoDelDia, totalAbonos, totalClientes } = calcularMetricasClientes(
    clientes,
    fechaHoyIso,
    fechaActual,
  );

  return {
    ganancias: saldoActual - saldoMasNuevo,
    gastosHoy,
    prestamoDelDia: prestamoDelDia + metricasMovimientosDelDia.prestamoDelDia,
    saldoActual,
    saldoMasNuevo,
    totalAbonos: totalAbonos + metricasMovimientosDelDia.totalAbonos,
    totalClientes,
    totalMonto1: totalIngresos,
    totalMonto2: totalSalidas,
  };
};

const METRICAS_MOVIMIENTOS_DEL_DIA_INICIALES = {
  prestamoDelDia: 0,
  totalAbonos: 0,
};

export const useEstadisticasModal = ({
  clientes,
  selectedRuta,
  setVerEstadisticas,
}: EstadisticasModalProps): UseEstadisticasModalReturn => {
  const [mostrarCierresCaja, setMostrarCierresCaja] = useState(false);
  const [mostrarMovimientos, setMostrarMovimientos] = useState(false);
  const fechaActual = useMemo(() => new Date(), []);
  const adminUid = selectedRuta?.adminUid;
  const rutaUid = selectedRuta?.uid;
  const canUseCierresCaja = Boolean(adminUid && rutaUid);
  const fechaHoyIso = useMemo(() => format(fechaActual, "dd/MM/yyyy"), [fechaActual]);
  const gastosHoyLegacy = calcularGastosDelDia(selectedRuta?.historialGastos);

  // Alta frecuencia (cada gasto registrado lo altera); vista de solo lectura
  // abierta bajo demanda (VerDetallesRutasModal -> "estadisticas"), staleTime
  // corto porque es dinero mostrado en pantalla.
  const { data: gastosHoy = gastosHoyLegacy } = useQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      try {
        const db = getFirestore();
        return await obtenerGastosDelDiaDeRuta({
          adminUid: adminUid as string,
          db,
          rutaUid: rutaUid as string,
        });
      } catch (error) {
        console.error("Error fetching gastos del dia desde Firebase:", error);
        return gastosHoyLegacy;
      }
    },
    queryKey: queryKeys.gastosDelDia(adminUid, rutaUid, fechaHoyIso),
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  // Misma key que ClientesProvider (queryKeys.movimientosDelDia): si el admin
  // esta viendo las estadisticas de su propia ruta activa, comparte cache en
  // vez de repetir la lectura.
  const { data: metricasMovimientosDelDia = METRICAS_MOVIMIENTOS_DEL_DIA_INICIALES } = useQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      try {
        const db = getFirestore();
        const inicioDia = new Date();
        inicioDia.setHours(0, 0, 0, 0);
        const finDia = new Date(inicioDia);
        finDia.setDate(finDia.getDate() + 1);
        const movimientosSnapshot = await getDocs(
          query(
            crearMovimientosRutaCollectionRef(db, adminUid as string, rutaUid as string),
            where("fecha", ">=", inicioDia),
            where("fecha", "<", finDia),
          ),
        );
        const movimientos = movimientosSnapshot.docs.map(
          (movimientoDoc) => movimientoDoc.data() as MovimientoRutaCompatible,
        );

        return calcularMetricasMovimientosDelDia(movimientos, inicioDia);
      } catch (error) {
        console.error("Error fetching movimientos del dia from Firebase:", error);
        return METRICAS_MOVIMIENTOS_DEL_DIA_INICIALES;
      }
    },
    queryKey: queryKeys.movimientosDelDia(adminUid, rutaUid, fechaHoyIso),
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  const metricas = useMemo(
    () =>
      calcularMetricasEstadisticas(
        selectedRuta,
        clientes,
        gastosHoy,
        metricasMovimientosDelDia,
        fechaHoyIso,
        fechaActual,
      ),
    [clientes, fechaActual, fechaHoyIso, gastosHoy, metricasMovimientosDelDia, selectedRuta],
  );

  const handleClose = useCallback(() => {
    setVerEstadisticas(false);
  }, [setVerEstadisticas]);

  const handleOpenCierresCaja = useCallback(() => {
    if (!canUseCierresCaja) {
      return;
    }

    setMostrarCierresCaja(true);
  }, [canUseCierresCaja]);

  const handleCloseCierresCaja = useCallback(() => {
    setMostrarCierresCaja(false);
  }, []);

  const handleOpenMovimientos = useCallback(() => {
    setMostrarMovimientos(true);
  }, []);

  const handleCloseMovimientos = useCallback(() => {
    setMostrarMovimientos(false);
  }, []);

  return {
    canUseCierresCaja,
    formatDate,
    formatearNumeros,
    handleClose,
    handleCloseCierresCaja,
    handleCloseMovimientos,
    handleOpenCierresCaja,
    handleOpenMovimientos,
    metricas,
    mostrarCierresCaja,
    mostrarMovimientos,
  };
};
